/**
 * Curated option data for onboarding selects. These are identifier values
 * (IANA timezone ids, ISO 4217 currency codes) — data, not translatable UI copy.
 */

export const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Moscow',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Dhaka',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Sydney',
  'Pacific/Auckland',
] as const;

export const CURRENCIES = [
  'USD',
  'EUR',
  'GBP',
  'INR',
  'PKR',
  'BDT',
  'AED',
  'SAR',
  'AUD',
  'CAD',
  'SGD',
  'JPY',
  'CNY',
  'ZAR',
  'BRL',
  'NGN',
  'KES',
] as const;

/** Legacy IANA aliases some browsers still report, mapped to their canonical id. */
const TIMEZONE_ALIASES: Readonly<Record<string, string>> = {
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
};

/** Normalize a timezone id to its canonical IANA form (e.g. Asia/Calcutta → Asia/Kolkata). */
export function canonicalTimezone(timezone: string): string {
  return TIMEZONE_ALIASES[timezone] ?? timezone;
}

/** Primary IANA timezone for a country, used to seed Timezone from the clinic phone country. */
const COUNTRY_PRIMARY_TIMEZONE: Readonly<Record<string, string>> = {
  US: 'America/New_York',
  CA: 'America/Toronto',
  BR: 'America/Sao_Paulo',
  GB: 'Europe/London',
  FR: 'Europe/Paris',
  DE: 'Europe/Berlin',
  RU: 'Europe/Moscow',
  EG: 'Africa/Cairo',
  ZA: 'Africa/Johannesburg',
  NG: 'Africa/Lagos',
  KE: 'Africa/Nairobi',
  AE: 'Asia/Dubai',
  SA: 'Asia/Riyadh',
  PK: 'Asia/Karachi',
  IN: 'Asia/Kolkata',
  BD: 'Asia/Dhaka',
  TH: 'Asia/Bangkok',
  SG: 'Asia/Singapore',
  CN: 'Asia/Shanghai',
  JP: 'Asia/Tokyo',
  KR: 'Asia/Seoul',
  AU: 'Australia/Sydney',
  NZ: 'Pacific/Auckland',
};

/** The primary timezone for a country code, if known. */
export function primaryTimezoneForCountry(countryCode: string): string | undefined {
  return COUNTRY_PRIMARY_TIMEZONE[countryCode];
}

/** Detect the browser's IANA timezone (canonicalized), falling back to UTC. */
export function detectTimezone(): string {
  try {
    return canonicalTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  } catch {
    return 'UTC';
  }
}

/** Merge a value into a list of options so a preselected value always renders. */
export function withValue(list: readonly string[], value: string): string[] {
  if (!value || list.includes(value)) {
    return [...list];
  }
  return [value, ...list];
}
